/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/stores/context.ts
 * 全局运营上下文（租户/数据范围/时间粒度）
 */

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

export interface OperatorOption {
  id: string
  name: string
}

export type PeriodPreset = 'today' | '7d' | '30d'

interface ScopeState {
  departments: string[]
  cities: string[]
  regions: string[]
}

const STORAGE_KEY = 'operator:context'

export const useContextStore = defineStore('operatorContext', () => {
  // 可选租户列表（后续从 API 同步）；这里给出最小占位
  const operatorList = ref<OperatorOption[]>([])
  const activeOperatorId = ref<string | null>(null)

  // 数据范围（最小占位，后续按接口返回结构扩展）
  const availableScopes = ref<ScopeState>({ departments: [], cities: [], regions: [] })
  const selectedScopes = ref<ScopeState>({ departments: [], cities: [], regions: [] })

  // 时间粒度/周期
  const period = ref<PeriodPreset>('today')

  const hasActiveOperator = computed(() => !!activeOperatorId.value)

  function setOperators(list: OperatorOption[], activeId?: string | null) {
    operatorList.value = list
    if (activeId != null) activeOperatorId.value = activeId
  }

  function setActiveOperator(id: string | null) {
    activeOperatorId.value = id
  }

  function setAvailableScopes(scopes: Partial<ScopeState>) {
    availableScopes.value = { ...availableScopes.value, ...scopes }
  }

  function setSelectedScopes(scopes: Partial<ScopeState>) {
    selectedScopes.value = { ...selectedScopes.value, ...scopes }
  }

  function setPeriod(p: PeriodPreset) {
    period.value = p
  }

  // 持久化（轻量本地存储）
  function load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const data = JSON.parse(raw)
      operatorList.value = data.operatorList || []
      activeOperatorId.value = data.activeOperatorId || null
      availableScopes.value = data.availableScopes || availableScopes.value
      selectedScopes.value = data.selectedScopes || selectedScopes.value
      period.value = data.period || period.value
    } catch {}
  }

  function save() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          operatorList: operatorList.value,
          activeOperatorId: activeOperatorId.value,
          availableScopes: availableScopes.value,
          selectedScopes: selectedScopes.value,
          period: period.value
        })
      )
    } catch {}
  }

  watch([activeOperatorId, selectedScopes, period], save, { deep: true })

  // 初始加载
  load()

  return {
    operatorList,
    activeOperatorId,
    availableScopes,
    selectedScopes,
    period,
    hasActiveOperator,
    setOperators,
    setActiveOperator,
    setAvailableScopes,
    setSelectedScopes,
    setPeriod
  }
})

